import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogOverlay } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import PlaylistSelector from './PlaylistSelector';
import { SET_DIALOG_OPEN_TWO, SET_SONG_TO_ADD_TWO } from '@/redux/features/music/playlistSlice';
import { SET_ALLUSERSONGS } from '@/redux/features/music/musicSlice';
import { useDispatch, useSelector } from 'react-redux';
import birdImg from "../../assets/greenbird.png"
import { ListPlus } from 'lucide-react';

const AddToPlaylistDialog = () => {
    const dispatch = useDispatch();
    const [selectedPlaylist, setSelectedPlaylist] = useState(null);
    const [isNewPlaylistDialogOpen, setIsNewPlaylistDialogOpen] = useState(false);
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [publish, setPublish] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const { songToAdd, dialogOpen } = useSelector(state => state.playlists);
    const { allUserSongs } = useSelector(state => state.music);
    // Enhanced cleanup function
    const cleanupPointerEvents = () => {
        document.body.style.removeProperty('pointer-events');
        document.body.style.cursor = 'auto';
    };
    // Enhanced onOpenChange handler for main dialog
    const onOpenChange = (value) => {
        dispatch(SET_DIALOG_OPEN_TWO(value));
        setSelectedPlaylist(null);
        if (!value) {
            cleanupPointerEvents();
        }
    };
    // Enhanced onOpenChange handler for new playlist dialog
    const onNewPlaylistDialogChange = (value) => {
        setIsNewPlaylistDialogOpen(value);
        if (!value) {
            cleanupPointerEvents();
        }
    };
    // Ensure cleanup is called on dialog close events
    const dialogCloseHandler = () => {
        cleanupPointerEvents();
    };
    // Enhanced handleAddSongToPlaylist with proper cleanup
    const handleAddSongToPlaylist = useCallback(async () => {
        if (!selectedPlaylist || !songToAdd?.song_id) {
            toast.error('Please select a playlist and song');
            return;
        }
        try {
            setIsAdding(true);
            const response = await axios.post('/playlist/add', {
                song_id: songToAdd.song_id, playlist_slug: selectedPlaylist.playlist_slug
            }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            const updatedSongs = allUserSongs.map(song => {
                if (song.song_id === songToAdd.song_id) {
                    const existingPlaylists = Array.isArray(song.playlists) ? song.playlists : [];
                    return {
                        ...song, playlists: [...existingPlaylists, selectedPlaylist]
                    };
                } return song;
            });
            dispatch(SET_ALLUSERSONGS(updatedSongs));
            if (response.data.status === 'Song already in the playlist') {
                toast.error(response.data.status);
            } else {
                toast.success('Song added to playlist successfully!');
            }
            dispatch(SET_DIALOG_OPEN_TWO(false));
            dispatch(SET_SONG_TO_ADD_TWO(null));
            setSelectedPlaylist(null);
            cleanupPointerEvents();
        } catch (error) {
            console.error('Error adding song to playlist:', error);
            toast.error(error.response?.data?.message || 'Failed to add song to playlist');
            cleanupPointerEvents();
        } finally {
            setIsAdding(false);
        }
    }, [selectedPlaylist, songToAdd, dispatch, allUserSongs]);

    const handleAddPlaylist = useCallback(async () => {
        if (newPlaylistName.trim() === '') {
            toast.error("Playlist name cannot be empty");
            return;
        }
        try {
            setIsAdding(true);
            // First, create the playlist
            await axios.post('/playlist/create', { name: newPlaylistName, publish }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            // Close the new playlist dialog
            setIsNewPlaylistDialogOpen(false);
            setNewPlaylistName('');
            dispatch(SET_DIALOG_OPEN_TWO(true));
            dispatch(SET_SONG_TO_ADD_TWO(songToAdd));
            cleanupPointerEvents();
        } catch (error) {
            console.log('error', error.response.data.detail);
            cleanupPointerEvents();
            if (error.response.status === 403) {
                toast.error('Playlist creation limit reached (10). Delete playlists or upgrade for more.');
            } else {
                toast.error(`Failed to create playlist: ${error.response.data.detail}`);
            }
        } finally {
            setIsAdding(false);
            cleanupPointerEvents();
        }
    }, [newPlaylistName, publish, songToAdd, dispatch]);

    // Add cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupPointerEvents();
        };
    }, []);

    return (
        <>
            <Dialog open={dialogOpen} onOpenChange={onOpenChange}>
                <DialogOverlay className="z-[999]">
                    <DialogContent className="z-[9999] w-[95%] sm:w-full max-w-sm sm:max-w-lg p-[0.6rem] bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 dark:from-pink-200 dark:to-pink-400 text-slate-200 dark:text-slate-800 border border-green-400/50 dark:border-pink-700 dark:border dark:border-1 rounded-lg" onCloseAutoFocus={dialogCloseHandler} onEscapeKeyDown={dialogCloseHandler} onInteractOutside={dialogCloseHandler}>
                        <DialogHeader className="space-y-2">
                            <div className="flex flex-col items-start gap-2">
                                <div className="flex items-center justify-between w-full">
                                    <DialogTitle className="text-lg font-semibold text-green-400 dark:text-pink-900 font-potta">
                                        Add Song to Playlist
                                    </DialogTitle>
                                    <button onClick={() => { onOpenChange(false); setIsNewPlaylistDialogOpen(true); }} className="flex items-center justify-center text-green-400 dark:text-black hover:text-green-300 dark:hover:text-black">
                                        <ListPlus className="text-2xl" />
                                    </button>
                                </div>
                                <DialogDescription className="text-sm text-slate-300 dark:text-pink-800 font-poppins">
                                    Confirm adding song to playlist
                                </DialogDescription>
                            </div>
                        </DialogHeader>
                        <div className="relative w-full flex flex-row items-center sm:items-start sm:gap-4 gap-2 bg-slate-900 dark:bg-pink-200/50 sm:p-3 p-1 rounded-lg shadow-md shadow-slate-800 dark:shadow-pink-300/20">
                            <img src={songToAdd?.image_url || birdImg} alt="Song Cover" className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex-shrink-0" />
                            <div className="flex-1 flex flex-col items-start sm:w-full sm:min-w-0 min-w-0 w-[30px] truncate">
                                {songToAdd?.song_name ?
                                    <p className="font-semibold text-slate-100 dark:text-pink-900">
                                        {songToAdd?.song_name}
                                    </p> :
                                    <div className="h-4 rounded-full my-1 w-5/6 bg-slate-800 dark:bg-pink-300/50"></div>
                                }
                                <p className="text-sm text-slate-400 dark:text-pink-700 truncate w-full">
                                    Playlist: {selectedPlaylist?.name || 'Not selected'}
                                </p>
                            </div>
                        </div>
                        <DialogFooter className="mt-4">
                            <div className="flex flex-col gap-4 sm:flex-row w-full">
                                <div className="flex-1 border border-green-400 dark:border-pink-400 rounded-md">
                                    <PlaylistSelector song={songToAdd} onSelectPlaylist={(selectedPlaylist) => { setSelectedPlaylist(selectedPlaylist); }} isDisabled={false} className={songToAdd ? "z-[999]" : "z-[999]"} />
                                </div>
                                <div className="flex justify-end gap-2 w-full sm:w-auto">
                                    <Button onClick={handleAddSongToPlaylist} className="w-full sm:w-auto bg-green-400 dark:bg-pink-800 hover:bg-green-300 dark:hover:bg-pink-500 text-slate-900 dark:text-slate-100">Add</Button>
                                    <DialogClose asChild>
                                        <Button variant="secondary" className="w-full sm:w-auto bg-slate-700 dark:bg-pink-100 hover:bg-slate-800 dark:hover:bg-pink-200/50 text-slate-300 dark:text-pink-900">Cancel</Button>
                                    </DialogClose>
                                </div>
                            </div>
                        </DialogFooter>
                    </DialogContent>
                </DialogOverlay>
            </Dialog>
            {/* New Playlist Dialog with enhanced cleanup */}
            <Dialog open={isNewPlaylistDialogOpen} onOpenChange={onNewPlaylistDialogChange}>
                <DialogOverlay className="z-[99]">
                    <DialogContent className="z-[999] bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 dark:from-pink-200 dark:to-pink-400 text-slate-300 dark:text-pink-900 border border-green-400 dark:border-pink-400 outline-1" onCloseAutoFocus={dialogCloseHandler} onEscapeKeyDown={dialogCloseHandler} onInteractOutside={dialogCloseHandler}>
                        <DialogHeader className="flex flex-col items-start">
                            <DialogTitle className="text-green-400 dark:text-pink-900">Create New Playlist</DialogTitle>
                            <DialogDescription className="text-slate-400 dark:text-slate-800 text-xs sm:text-md">
                                Fill in the details for your new playlist
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <input type="text" placeholder="Playlist Name..." value={newPlaylistName} onChange={(e) => setNewPlaylistName(e.target.value)} className="w-full px-2 py-3 rounded bg-slate-800 dark:bg-pink-200/70 text-sm text-slate-200 dark:text-pink-950 placeholder-slate-400 dark:placeholder-pink-700 focus:outline-none focus:ring-1 focus:ring-green-400 dark:focus:ring-pink-400 focus:border-transparent" />
                            <label className="switch flex items-center justify-between border-b pb-2 border-slate-700 dark:border-pink-300/50 cursor-pointer">
                                <p className="font-semibold text-sm text-slate-200 dark:text-pink-950">Publish</p>
                                <input checked={publish} onChange={(e) => setPublish(e.target.checked)} type="checkbox" />
                                <span className="slider dark:bg-pink-100/80"></span>
                            </label>
                        </div>
                        <DialogFooter>
                            <div className='flex flex-row justify-end gap-2'>
                                <Button onClick={handleAddPlaylist} disabled={isAdding} className="bg-green-400 dark:bg-pink-900 hover:bg-green-300 dark:hover:bg-pink-800 text-slate-800 dark:text-white px-8">
                                    {isAdding ? 'Creating...' : 'Save'}
                                </Button>
                                <DialogClose asChild>
                                    <Button className="bg-slate-700 dark:bg-pink-100/70 hover:bg-slate-800 dark:hover:bg-pink-100/60  text-slate-300 dark:text-pink-900 px-6">
                                        Cancel
                                    </Button>
                                </DialogClose>
                            </div>
                        </DialogFooter>
                    </DialogContent>
                </DialogOverlay>
            </Dialog>
        </>
    );
};

export default AddToPlaylistDialog;